// Camada de query do ledger admin (`spec/tasks/WEB-25.md`, `spec/17-frontend.md` §8.2).
//
// Duas rotas e nada mais: `GET /admin/v1/ledger/entries` (extrato completo, filtros
// combináveis e cursor) e `POST /admin/v1/ledger/adjustments` (ajuste manual, step-up).
// Nenhum tipo de resposta escrito à mão (`spec/17` §5.2): tudo sai de `types/admin.d.ts`.

import { infiniteQueryOptions } from '@tanstack/react-query';
import type { Mode } from '../../app/mode.tsx';
import { api } from '../client.ts';
import type { paths } from '../types/admin.d.ts';

export type AdminLedgerPage =
  paths['/admin/v1/ledger/entries']['get']['responses'][200]['content']['application/json'];

export type AdminLedgerEntry = AdminLedgerPage['data'][number];
export type AdminLedgerLine = AdminLedgerEntry['lines'][number];
export type AdminLedgerKind = AdminLedgerEntry['kind'];

export type AdjustmentBody =
  paths['/admin/v1/ledger/adjustments']['post']['requestBody']['content']['application/json'];

export type AdjustmentResponse =
  paths['/admin/v1/ledger/adjustments']['post']['responses'][201]['content']['application/json'];

export type AdjustmentLine = AdjustmentBody['lines'][number];
export type AdjustmentAccount = AdjustmentLine['account'];

/** Os dez `kind` de `ledger_entries` (`spec/02` §4.3, split de `spec/19`), na ordem do enum da API. */
export const ADMIN_LEDGER_KINDS = [
  'transaction.paid',
  'reserve.released',
  'withdrawal.requested',
  'withdrawal.settled',
  'withdrawal.partner_cost',
  'withdrawal.failed',
  'transaction.refunded',
  'transaction.chargeback',
  'adjustment',
  'split.settled',
  'split.reversed',
  'latam.paid',
  'latam.settled',
  'latam.reserve_released',
  'latam.fee_adjusted',
  'intl_withdrawal.requested',
  'intl_withdrawal.paid',
  'intl_withdrawal.reversed',
] as const satisfies readonly AdminLedgerKind[];

export function isAdminLedgerKind(value: string): value is AdminLedgerKind {
  return ADMIN_LEDGER_KINDS.some((kind) => kind === value);
}

/**
 * CONTRATO DE QUERY KEY (`app/live-events.ts`): domínio primeiro, `mode` depois. O mapa
 * de §4.5 invalida `['ledger']` por prefixo, `ledger.adjusted`, `transaction.*` e
 * `withdrawal.*` alcançam esta tela porque a key começa aqui.
 */
export const LEDGER_KEY = 'ledger';

/** `limit` default de `spec/00` §4.6. */
export const LEDGER_LIMIT = 50;

/** Fallback obrigatório de §4.5 enquanto o SSE não é alcançável (`FIX-CORS-SSE`). */
const POLL_MS = 30_000;

export interface AdminLedgerFilters {
  /** `sel_…`; ausente = todos os sellers. */
  sellerId: string | undefined;
  kind: AdminLedgerKind | undefined;
  /** `YYYY-MM-DD` em `America/Sao_Paulo` (`spec/13` §4.1). */
  from: string | undefined;
  to: string | undefined;
}

export function adminLedgerQueryKey(mode: Mode, filters: AdminLedgerFilters) {
  return [LEDGER_KEY, mode, 'admin', filters] as const;
}

export function adminLedgerQueryOptions(mode: Mode, filters: AdminLedgerFilters) {
  return infiniteQueryOptions({
    queryKey: adminLedgerQueryKey(mode, filters),
    queryFn: ({ pageParam }) =>
      api<AdminLedgerPage>('/admin/v1/ledger/entries', {
        query: {
          mode,
          limit: LEDGER_LIMIT,
          seller_id: filters.sellerId,
          kind: filters.kind,
          from: filters.from,
          to: filters.to,
          ...(pageParam === undefined ? {} : { cursor: pageParam }),
        },
      }),
    initialPageParam: undefined as string | undefined,
    // `next_cursor` nulo = fim da lista (`spec/00` §4.6); `undefined` é o que o Query
    // entende como "não tem próxima página".
    getNextPageParam: (page: AdminLedgerPage) => page.next_cursor ?? undefined,
    refetchInterval: POLL_MS,
  });
}

export function flattenEntries(pages: AdminLedgerPage[] | undefined): AdminLedgerEntry[] {
  return (pages ?? []).flatMap((page) => page.data);
}

/**
 * Ajuste manual. `403 mfa_required` é resolvido pelo `StepUpDialog` dentro de
 * `api/client.ts`, que reexecuta a chamada, esta camada não sabe que o step-up existe.
 * `201` (lançou) e `200` (mesma `reference_id`, replay idempotente) têm o mesmo corpo.
 */
export async function postAdjustment(body: AdjustmentBody): Promise<AdjustmentResponse> {
  return api<AdjustmentResponse>('/admin/v1/ledger/adjustments', { method: 'POST', body });
}
